// ---------------------------------------------------------------------------
// Shared number/date formatting utilities.
//
// tio-ui is the base addon (tio-data, tio-common and tio-reporting all depend
// on it, not the reverse), so this is the one place every package can share a
// formatter from.
// ---------------------------------------------------------------------------

/**
 * Cached `Intl.NumberFormat` for USD amounts.
 *
 * Construction is the expensive part of `Intl`, so formatters are memoized per
 * locale + fraction-digit pair rather than rebuilt per call.
 *
 * `locale` is explicit on purpose. Call sites historically disagreed — some
 * hardcoded `'en-US'`, one passed the active ember-intl locale, one passed
 * `undefined` to follow the environment — and those produce different output
 * for the es-mx and fr-ca locales the apps ship. Each caller keeps its
 * existing choice here; unifying them is a user-visible decision, not a
 * refactor. Pass `undefined` to follow the runtime default.
 *
 * In templates prefer the `safe-format-number` helper: it routes through
 * ember-intl and so tracks the active locale automatically.
 */
const currencyFormatters = new Map<string, Intl.NumberFormat>();

export function currencyFormatter(
  locale: string | undefined,
  fractionDigits = 2
): Intl.NumberFormat {
  const key = `${locale ?? ''}|${fractionDigits}`;
  let formatter = currencyFormatters.get(key);

  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    currencyFormatters.set(key, formatter);
  }

  return formatter;
}

/**
 * Format a number as a US currency string (e.g., "$42,000.00").
 *
 * Two fraction digits by default; pass `fractionDigits` to override (e.g., 0
 * for whole-dollar display). Nullish and NaN amounts render as zero rather
 * than blowing up a template.
 */
export function formatCurrency(amount: number | undefined | null, fractionDigits = 2): string {
  const value = amount == null || Number.isNaN(amount) ? 0 : amount;

  return currencyFormatter('en-US', fractionDigits).format(value);
}

/**
 * Format an integer cent amount as a US currency string.
 *
 * The API returns most money as cents, so this saves each call site from
 * repeating the `/ 100`.
 */
export function formatCentsAsCurrency(
  cents: number | undefined | null,
  fractionDigits = 2
): string {
  return formatCurrency(cents == null || Number.isNaN(cents) ? 0 : cents / 100, fractionDigits);
}

/**
 * Format a date as "Jan 2025" (short month + year).
 */
export function formatMonthYear(date: Date | string | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(d);
}

/**
 * Format a date as "Jan 15, 2025" (short month + day + year).
 */
export function formatShortDate(date: Date | string | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}

/**
 * Format a date as "1/15/2025" (numeric month/day/year).
 */
export function formatNumericDate(date: Date | string | undefined | null): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'numeric',
    day: '2-digit',
    year: 'numeric',
  }).format(d);
}

/**
 * Format a benefit cycle string ("YYYY-MM") as "Jan 2025" (short month + year).
 *
 * Falls back to standard date parsing for non-cycle strings.
 */
export function formatCycleDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';

  // Handle "YYYY-MM" format (e.g., "2026-01")
  const dashParts = dateStr.split('-');
  if (dashParts.length >= 2) {
    const date = new Date(Number(dashParts[0]), Number(dashParts[1]) - 1);
    if (!isNaN(date.getTime())) {
      return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date);
    }
  }

  // Handle accounting period "YYYYMmm" format (e.g., "2026M01")
  const periodMatch = dateStr.match(/^(\d{4})M(\d{2})$/);
  if (periodMatch) {
    const date = new Date(Number(periodMatch[1]), Number(periodMatch[2]) - 1);
    if (!isNaN(date.getTime())) {
      return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date);
    }
  }

  // Fallback: try standard date parsing
  const fallback = new Date(dateStr);
  if (isNaN(fallback.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(fallback);
}

/**
 * Format a benefit cycle string ("YYYY-MM") as "December 2025".
 *
 * Uses the full month name followed by the year.
 * Falls back to standard date parsing for non-cycle strings.
 */
export function formatCycleDateLong(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';

  // Handle "YYYY-MM" format (e.g., "2025-12")
  const dashParts = dateStr.split('-');
  if (dashParts.length >= 2) {
    const monthNum = Number(dashParts[1]);
    if (monthNum >= 1 && monthNum <= 12) {
      const date = new Date(Number(dashParts[0]), monthNum - 1);
      if (!isNaN(date.getTime())) {
        const monthYear = new Intl.DateTimeFormat('en-US', {
          month: 'long',
          year: 'numeric',
        }).format(date);
        return monthYear;
      }
    }
  }

  // Handle accounting period "YYYYMmm" format (e.g., "2025M12")
  const periodMatch = dateStr.match(/^(\d{4})M(\d{2})$/);
  if (periodMatch) {
    const monthNum = Number(periodMatch[2]);
    if (monthNum >= 1 && monthNum <= 12) {
      const date = new Date(Number(periodMatch[1]), monthNum - 1);
      if (!isNaN(date.getTime())) {
        const monthYear = new Intl.DateTimeFormat('en-US', {
          month: 'long',
          year: 'numeric',
        }).format(date);
        return monthYear;
      }
    }
  }

  // Fallback: try standard date parsing
  const fallback = new Date(dateStr);
  if (isNaN(fallback.getTime())) return '—';
  const monthYear = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    fallback
  );
  return monthYear;
}
